import { useEffect, useMemo, useState } from "react";
import { Send, Sparkles, PhoneCall, PenLine, Reply } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ActionCard from "../components/ActionCard";
import ProspectDrawer from "../components/ProspectDrawer";

const Section = ({ icon: Icon, title, subtitle, accent, count, testId, children, empty, loading }) => (
  <div className="glass-panel p-5 fade-up" data-testid={testId}>
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold text-white">{title}</h2>
          <span className="text-xs font-mono-tech text-slate-400 bg-white/5 border border-white/10 rounded-full px-2 py-0.5" data-testid={`${testId}-count`}>{count}</span>
        </div>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
    </div>
    {count === 0 ? (
      <p className="text-sm text-slate-600 py-4 text-center">{loading ? "Loading…" : empty}</p>
    ) : (
      <div className="grid md:grid-cols-2 gap-3">{children}</div>
    )}
  </div>
);

export default function Today() {
  const { user } = useAuth();
  const [prospects, setProspects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/prospects").then(({ data }) => { setProspects(data); setLoaded(true); });
  useEffect(() => {
    load();
  }, []);

  const [prepping, setPrepping] = useState(false);
  const prepBriefings = async () => {
    setPrepping(true);
    try {
      const { data } = await api.post("/research/run-all");
      toast.success(data.queued > 0 ? `Preparing ${data.queued} briefings — they'll fill in shortly` : "All briefings already prepared");
      setTimeout(load, 12000);
    } catch {
      toast.error("Could not start research");
    } finally {
      setPrepping(false);
    }
  };

  const [drafting, setDrafting] = useState(false);
  const draftAll = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/outreach/run-all");
      toast.success(data.queued > 0 ? `Drafting ${data.queued} replies — they'll fill in shortly` : "All replies already drafted");
      setTimeout(load, 15000);
    } catch {
      toast.error("Could not start drafting");
    } finally {
      setDrafting(false);
    }
  };

  const [checkingReplies, setCheckingReplies] = useState(false);
  const checkReplies = async () => {
    setCheckingReplies(true);
    try {
      const { data } = await api.post("/gmail/check-replies");
      toast.success(data.hot > 0 ? `${data.hot} hot thread${data.hot > 1 ? "s" : ""} — replied prospects moved up top` : "No new replies right now");
      load();
    } catch (e) {
      if (e?.response?.status === 428) toast.message(e?.response?.data?.detail || "Connect Gmail to detect replies");
      else toast.error("Could not check replies");
    } finally {
      setCheckingReplies(false);
    }
  };

  const onGenerated = (id, type, copy) => {
    setProspects((prev) => prev.map((p) => (p.id === id ? { ...p, outreach_copy: { ...(p.outreach_copy || {}), [type]: copy } } : p)));
  };

  const updateLocal = (updated) => {
    setProspects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setSelected((s) => (s && s.id === updated.id ? updated : s));
  };

  const advanceFollowUp = async (p) => {
    try {
      const { data } = await api.post(`/prospects/${p.id}/follow-up/sent`);
      updateLocal(data);
      if (data.next_follow_up_at) {
        const d = new Date(data.next_follow_up_at).toLocaleDateString([], { month: "short", day: "numeric" });
        toast.success(`Logged — next touch reminder set for ${d}`);
      } else {
        toast.success("Logged — follow-up sequence complete, no more reminders");
      }
    } catch {
      toast.error("Could not log follow-up");
    }
  };

  const patchProspect = async (id, updates, msg) => {
    const { data } = await api.patch(`/prospects/${id}`, updates);
    setProspects((prev) => prev.map((p) => (p.id === data.id ? data : p)));
    setSelected((s) => (s && s.id === data.id ? data : s));
    if (msg) toast.success(msg);
    return data;
  };

  const markContacted = (p) => {
    if (p.stage === "new_lead") patchProspect(p.id, { stage: "follow_up_book" });
  };

  const typeForStage = (s) =>
    s === "follow_up_book" ? "book" : s === "follow_up_close" ? "close_deal" : s === "call_scheduled" ? "close" : "reach_out";

  const { close, reach, book, closeDeal, replies } = useMemo(() => {
    const close = prospects
      .filter((p) => p.stage === "call_scheduled")
      .sort((a, b) => new Date(a.call_time || 0) - new Date(b.call_time || 0));
    const reach = prospects.filter((p) => p.stage === "new_lead" && !p.call_time);
    const endToday = new Date(); endToday.setHours(23, 59, 59, 999);
    const due = (stage) => prospects
      .filter((p) => p.stage === stage && p.next_follow_up_at && new Date(p.next_follow_up_at) <= endToday)
      .sort((a, b) => new Date(a.next_follow_up_at) - new Date(b.next_follow_up_at));
    const replies = prospects
      .filter((p) => p.reply_received && !["closed_won", "closed_lost"].includes(p.stage))
      .sort((a, b) => new Date(b.last_reply_at || 0) - new Date(a.last_reply_at || 0));
    return { close, reach, book: due("follow_up_book"), closeDeal: due("follow_up_close"), replies };
  }, [prospects]);

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  })();
  const firstName = (user?.name || "").split(" ")[0];
  const today = new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="space-y-6" data-testid="today-page">
      <div className="fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">Daily Briefing</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
            {greeting}{firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            {today} · <span className="text-rose-300">{replies.length}</span> replied · <span className="text-sky-300">{reach.length}</span> to reach out · <span className="text-[#9cc6f0]">{close.length}</span> calls to prep · <span className="text-amber-300">{book.length}</span> to book · <span className="text-teal-300">{closeDeal.length}</span> to close
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="check-replies-btn"
            onClick={checkReplies}
            disabled={checkingReplies}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-sm font-semibold transition-colors disabled:opacity-50"
          >
            <Reply size={15} /> Check replies
          </button>
          <button
            data-testid="draft-replies-btn"
            onClick={draftAll}
            disabled={drafting}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#4690d9]/40 text-[#9cc6f0] hover:bg-[#4690d9]/10 text-sm font-semibold transition-colors disabled:opacity-50"
          >
            <PenLine size={15} /> Draft replies
          </button>
          <button
            data-testid="prep-briefings-btn"
            onClick={prepBriefings}
            disabled={prepping}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#4690d9]/40 text-[#9cc6f0] hover:bg-[#4690d9]/10 text-sm font-semibold transition-colors disabled:opacity-50"
          >
            <Sparkles size={15} /> Prep briefings
          </button>
        </div>
      </div>

      <Section
        icon={Reply}
        title="Replies · Hot"
        subtitle="Prospects who replied to your email — respond to these first"
        accent="bg-rose-500/15 text-rose-400"
        count={replies.length}
        loading={!loaded}
        testId="section-replies"
        empty="No new replies right now. Hit Check replies to refresh."
      >
        {replies.map((p) => (
          <ActionCard key={p.id} prospect={p} type={typeForStage(p.stage)} onView={setSelected} onSent={advanceFollowUp} onLogFollowUp={["follow_up_book", "follow_up_close"].includes(p.stage) ? advanceFollowUp : undefined} onGenerated={onGenerated} onUpdated={updateLocal} />
        ))}
      </Section>

      <Section
        icon={PhoneCall}
        title="Calls to prep"
        subtitle="Upcoming booked calls — prep, confirm, then log the outcome"
        accent="bg-[#4690d9]/15 text-[#4690d9]"
        count={close.length}
        loading={!loaded}
        testId="section-close"
        empty="No calls on the calendar right now."
      >
        {close.map((p) => (
          <ActionCard key={p.id} prospect={p} type="close" onView={setSelected} onGenerated={onGenerated} onUpdated={updateLocal} />
        ))}
      </Section>

      <Section
        icon={Send}
        title="Reach out"
        subtitle="New leads waiting for first contact"
        accent="bg-sky-500/15 text-sky-400"
        count={reach.length}
        loading={!loaded}
        testId="section-reach-out"
        empty="You're all caught up — every new lead has been contacted."
      >
        {reach.map((p) => (
          <ActionCard key={p.id} prospect={p} type="reach_out" onView={setSelected} onSent={markContacted} onGenerated={onGenerated} onUpdated={updateLocal} />
        ))}
      </Section>

      <Section
        icon={PenLine}
        title="Get them booked"
        subtitle="Contacted but no call yet — follow-ups due today to book the call"
        accent="bg-amber-500/15 text-amber-400"
        count={book.length}
        loading={!loaded}
        testId="section-book"
        empty="No booking follow-ups due today. You're on top of it."
      >
        {book.map((p) => (
          <ActionCard key={p.id} prospect={p} type="book" onView={setSelected} onSent={advanceFollowUp} onLogFollowUp={advanceFollowUp} onGenerated={onGenerated} onUpdated={updateLocal} />
        ))}
      </Section>

      <Section
        icon={Sparkles}
        title="Close the deal"
        subtitle="Had their call — follow-ups due today to close them"
        accent="bg-teal-500/15 text-teal-400"
        count={closeDeal.length}
        loading={!loaded}
        testId="section-close-deal"
        empty="No closing follow-ups due today."
      >
        {closeDeal.map((p) => (
          <ActionCard key={p.id} prospect={p} type="close_deal" onView={setSelected} onSent={advanceFollowUp} onLogFollowUp={advanceFollowUp} onGenerated={onGenerated} onUpdated={updateLocal} />
        ))}
      </Section>

      <ProspectDrawer
        prospect={selected}
        onClose={() => setSelected(null)}
        onPatch={patchProspect}
        onUpdate={updateLocal}
        onDeleted={(id) => {
          setProspects((prev) => prev.filter((p) => p.id !== id));
          setSelected(null);
          toast.success("Prospect deleted");
        }}
      />
    </div>
  );
}
